import React, { useEffect, useRef, useState } from "react";

// 給与明細PDFをアプリ内でレンダリングするビューア。
// Chrome内蔵PDFビューア（iframe埋め込み）はツールバーが消せず・幅フィットも効かないため、
// PDF.js で canvas に自前描画し、ツールバー無し・横幅フィットの表示にする。
//
// PDF.js は CDN(cdnjs) から読み込む（npm依存を足すとサーバー自動デプロイの
// package-lock churn 衝突バグに触れて pull abort → デプロイ失敗するため。既存でも
// cdnjs は利用中）。PDF本体は url を XHR 取得するので、本番の別オリジン署名URLでは
// バケット側の CORS 許可が必要。失敗時は呼び出し側の「新しいタブで開く/DL」に委ねる。
const PDFJS_VERSION = "3.11.174";
const PDFJS_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

// pdf.js スクリプトは一度だけ読み込む（Promiseをモジュールに保持して共有）。
let pdfjsPromise = null;
function loadPdfjs() {
  if (typeof window !== "undefined" && window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PDFJS_SRC;
    s.async = true;
    s.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        resolve(window.pdfjsLib);
      } else {
        pdfjsPromise = null;
        reject(new Error("pdf.js の読み込みに失敗しました"));
      }
    };
    s.onerror = () => { pdfjsPromise = null; reject(new Error("pdf.js の読み込みに失敗しました")); };
    document.head.appendChild(s);
  });
  return pdfjsPromise;
}

// url のPDFを全ページ canvas に描画する。status: loading | ready | error。
export default function PdfViewer({ url }) {
  const pagesRef = useRef(null); // canvas を差し込む専用コンテナ（Reactは子を入れない）
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const pages = pagesRef.current;
    if (pages) pages.replaceChildren();
    setStatus("loading");

    (async () => {
      try {
        const pdfjsLib = await loadPdfjs();
        const pdf = await pdfjsLib.getDocument({
          url,
          // 非埋め込みの日本語CIDフォント（例: HeiseiKakuGo-W5 + /Encoding UniJIS-UCS2-H）は
          // CMapを渡さないと文字コードを解決できず、テキストが描画されない（罫線だけ出る）。
          // cdnjs はCMapを配信していない(403)ため、CMap/標準フォントは jsdelivr の
          // pdfjs-dist（同バージョン）から取得する。
          cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/cmaps/`,
          cMapPacked: true,
          // 非埋め込みの標準14フォント（Helvetica等＝数字・英字）の描画用データ。
          standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/standard_fonts/`,
        }).promise;
        if (cancelled) return;

        const containerWidth = (pagesRef.current && pagesRef.current.clientWidth) || 680;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = containerWidth / base.width;
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.className = "mp-pdf-page";
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          if (cancelled) return;
          if (pagesRef.current) pagesRef.current.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        }
        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="mp-pdf" data-status={status}>
      {status === "loading" && <div className="mp-pdf-msg">読み込んでいます…</div>}
      {status === "error" && (
        <div className="mp-pdf-msg">
          プレビューを表示できませんでした。下の「新しいタブで開く」からご覧ください。
        </div>
      )}
      <div className="mp-pdf-pages" ref={pagesRef} />
    </div>
  );
}
